import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["onChange", "defaultIndex", "index", "allowMultiple", "allowToggle"],
  _excluded2 = ["isDisabled", "isFocusable", "id"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useAccordionDescendant, useAccordionDescendants } from "./chunk-JST25EWU.mjs";

// src/use-accordion.ts
import { createContext } from "@chakra-ui/react-context";
import { useControllableState } from "@chakra-ui/react-use-controllable-state";
import { mergeRefs } from "@chakra-ui/react-use-merge-refs";
import { callAllHandlers, warn } from "@chakra-ui/shared-utils";
import { useCallback, useEffect, useId, useRef, useState } from "react";
function useAccordion(props) {
  var onChange = props.onChange,
    defaultIndex = props.defaultIndex,
    indexProp = props.index,
    allowMultiple = props.allowMultiple,
    allowToggle = props.allowToggle,
    htmlProps = _objectWithoutProperties(props, _excluded);
  allowMultipleWarning(props);
  allowMultipleAndAllowToggleWarning(props);
  var descendants = useAccordionDescendants();
  var _useState = useState(-1),
    _useState2 = _slicedToArray(_useState, 2),
    focusedIndex = _useState2[0],
    setFocusedIndex = _useState2[1];
  useEffect(function () {
    return function () {
      setFocusedIndex(-1);
    };
  }, []);
  var _useControllableState = useControllableState({
      value: indexProp,
      defaultValue: function defaultValue() {
        if (allowMultiple) return defaultIndex != null ? defaultIndex : [];
        return defaultIndex != null ? defaultIndex : -1;
      },
      onChange: onChange
    }),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    index = _useControllableState2[0],
    setIndex = _useControllableState2[1];
  var getAccordionItemProps = function getAccordionItemProps(idx) {
    var isOpen = false;
    if (idx !== null) {
      isOpen = Array.isArray(index) ? index.includes(idx) : index === idx;
    }
    var onChange2 = function onChange2(isOpen2) {
      if (idx === null) return;
      if (allowMultiple && Array.isArray(index)) {
        var nextState = isOpen2 ? index.concat(idx) : index.filter(function (i) {
          return i !== idx;
        });
        setIndex(nextState);
      } else if (isOpen2) {
        setIndex(idx);
      } else if (allowToggle) {
        setIndex(-1);
      }
    };
    return {
      isOpen: isOpen,
      onChange: onChange2
    };
  };
  return {
    index: index,
    setIndex: setIndex,
    htmlProps: htmlProps,
    getAccordionItemProps: getAccordionItemProps,
    focusedIndex: focusedIndex,
    setFocusedIndex: setFocusedIndex,
    descendants: descendants
  };
}
var _createContext = createContext({
    name: "AccordionContext",
    hookName: "useAccordionContext",
    providerName: "Accordion"
  }),
  _createContext2 = _slicedToArray(_createContext, 2),
  AccordionProvider = _createContext2[0],
  useAccordionContext = _createContext2[1];
function useAccordionItem(props) {
  var isDisabled = props.isDisabled,
    isFocusable = props.isFocusable,
    id = props.id,
    htmlProps = _objectWithoutProperties(props, _excluded2);
  var _useAccordionContext = useAccordionContext(),
    getAccordionItemProps = _useAccordionContext.getAccordionItemProps,
    setFocusedIndex = _useAccordionContext.setFocusedIndex;
  var buttonRef = useRef(null);
  var reactId = useId();
  var uid = id != null ? id : reactId;
  var buttonId = "accordion-button-".concat(uid);
  var panelId = "accordion-panel-".concat(uid);
  focusableNotDisabledWarning(props);
  var _useAccordionDescenda = useAccordionDescendant({
      disabled: isDisabled && !isFocusable
    }),
    register = _useAccordionDescenda.register,
    index = _useAccordionDescenda.index,
    descendants = _useAccordionDescenda.descendants;
  var _getAccordionItemProp = getAccordionItemProps(index === -1 ? null : index),
    isOpen = _getAccordionItemProp.isOpen,
    onChange = _getAccordionItemProp.onChange;
  warnIfOpenAndDisabled({
    isOpen: isOpen,
    isDisabled: isDisabled
  });
  var onOpen = function onOpen() {
    onChange == null ? void 0 : onChange(true);
  };
  var onClose = function onClose() {
    onChange == null ? void 0 : onChange(false);
  };
  var onClick = useCallback(function () {
    onChange == null ? void 0 : onChange(!isOpen);
    setFocusedIndex(index);
  }, [index, setFocusedIndex, isOpen, onChange]);
  var onKeyDown = useCallback(function (event) {
    var keyMap = {
      ArrowDown: function ArrowDown() {
        var next = descendants.nextEnabled(index);
        next == null ? void 0 : next.node.focus();
      },
      ArrowUp: function ArrowUp() {
        var prev = descendants.prevEnabled(index);
        prev == null ? void 0 : prev.node.focus();
      },
      Home: function Home() {
        var first = descendants.firstEnabled();
        first == null ? void 0 : first.node.focus();
      },
      End: function End() {
        var last = descendants.lastEnabled();
        last == null ? void 0 : last.node.focus();
      }
    };
    var action = keyMap[event.key];
    if (action) {
      event.preventDefault();
      action(event);
    }
  }, [descendants, index]);
  var onFocus = useCallback(function () {
    setFocusedIndex(index);
  }, [setFocusedIndex, index]);
  var getButtonProps = useCallback(function getButtonProps2() {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props2), {}, {
      type: "button",
      ref: mergeRefs(register, buttonRef, ref),
      id: buttonId,
      disabled: !!isDisabled,
      "aria-expanded": !!isOpen,
      "aria-controls": panelId,
      onClick: callAllHandlers(props2.onClick, onClick),
      onFocus: callAllHandlers(props2.onFocus, onFocus),
      onKeyDown: callAllHandlers(props2.onKeyDown, onKeyDown)
    });
  }, [buttonId, isDisabled, isOpen, onClick, onFocus, onKeyDown, panelId, register]);
  var getPanelProps = useCallback(function getPanelProps2() {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props2), {}, {
      ref: ref,
      role: "region",
      id: panelId,
      "aria-labelledby": buttonId,
      hidden: !isOpen
    });
  }, [buttonId, isOpen, panelId]);
  return {
    isOpen: isOpen,
    isDisabled: isDisabled,
    isFocusable: isFocusable,
    onOpen: onOpen,
    onClose: onClose,
    getButtonProps: getButtonProps,
    getPanelProps: getPanelProps,
    htmlProps: htmlProps
  };
}
function allowMultipleWarning(props) {
  var index = props.index || props.defaultIndex;
  var condition = index != null && !Array.isArray(index) && props.allowMultiple;
  warn({
    condition: !!condition,
    message: "If 'allowMultiple' is passed, then 'index' or 'defaultIndex' must be an array. You passed: ".concat(typeof index, ",")
  });
}
function allowMultipleAndAllowToggleWarning(props) {
  warn({
    condition: !!(props.allowMultiple && props.allowToggle),
    message: "If 'allowMultiple' is passed, 'allowToggle' will be ignored. Either remove 'allowToggle' or 'allowMultiple' depending on whether you want multiple accordions visible or not"
  });
}
function focusableNotDisabledWarning(props) {
  warn({
    condition: !!(props.isFocusable && !props.isDisabled),
    message: "Using only 'isFocusable', this prop is reserved for situations where you pass 'isDisabled' but you still want the element to receive focus (A11y). Either remove it or pass 'isDisabled' as well.\n    "
  });
}
function warnIfOpenAndDisabled(props) {
  warn({
    condition: props.isOpen && !!props.isDisabled,
    message: "Cannot open a disabled accordion item"
  });
}
export { useAccordion, AccordionProvider, useAccordionContext, useAccordionItem };